
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useFastingHistory } from '@/contexts/fasting/FastingHistoryContext';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent
} from '@/components/ui/chart';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  ResponsiveContainer
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface AnalyticsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AnalyticsModal: React.FC<AnalyticsModalProps> = ({
  open,
  onOpenChange
}) => {
  const { sessions, getCompletedFasts } = useFastingHistory();
  
  // Prepare data for charts
  const lastTwoWeeks = [...sessions]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-14);
  
  // Group by date for duration chart
  const durationByDate = lastTwoWeeks.reduce((acc: Record<string, {date: string, duration: number}>, session) => {
    const hours = session.duration / 3600;
    if (!acc[session.date]) {
      acc[session.date] = { date: session.date, duration: hours };
    } else {
      acc[session.date].duration = Math.max(acc[session.date].duration, hours);
    }
    return acc;
  }, {});
  
  const durationData = Object.values(durationByDate);
  
  // Protocol distribution chart data
  const protocolCounts: Record<string, number> = {};
  sessions.forEach(session => {
    if (!protocolCounts[session.protocolName]) {
      protocolCounts[session.protocolName] = 0;
    }
    protocolCounts[session.protocolName]++;
  });
  
  const protocolData = Object.entries(protocolCounts).map(([name, count]) => ({
    name,
    count
  }));
  
  // Completion rate
  const completionRate = sessions.length > 0 
    ? Math.round((getCompletedFasts() / sessions.length) * 100) 
    : 0;
    
  // Days with fasts in the last month
  const uniqueDays = new Set(sessions
    .filter(s => {
      const sessionDate = new Date(s.date);
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return sessionDate >= monthAgo;
    })
    .map(s => s.date)
  );
  
  const chartConfig = {
    duration: {
      label: "Hours"
    },
    count: {
      label: "Count"
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Detailed Analytics</DialogTitle>
          <DialogDescription>
            View detailed statistics about your fasting journey
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-foreground">{completionRate}%</div>
              <p className="text-xs text-muted-foreground">Completion Rate</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-foreground">{uniqueDays.size}</div>
              <p className="text-xs text-muted-foreground">Active Days (Last Month)</p>
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="duration">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="duration">Duration Trends</TabsTrigger>
            <TabsTrigger value="protocols">Protocol Distribution</TabsTrigger>
          </TabsList>
          
          <TabsContent value="duration" className="space-y-4">
            <ChartContainer config={chartConfig} className="w-full aspect-[4/3] mt-6">
              <LineChart data={durationData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                <Tooltip labelFormatter={(value) => `Date: ${value}`} />
                <Line 
                  type="monotone" 
                  dataKey="duration" 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }} 
                  name="Fasting Duration (hours)"
                />
              </LineChart>
            </ChartContainer>
          </TabsContent>
          
          <TabsContent value="protocols">
            <ChartContainer config={chartConfig} className="w-full aspect-[4/3] mt-6">
              <BarChart data={protocolData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Bar dataKey="count" name="Fasts Completed" fill="#82ca9d" />
              </BarChart>
            </ChartContainer>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
